import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import Back from 'components/Back/index.tsx';
import IterationDetailsForm from 'components/forms/IterationDetails/IterationDetailsForm.tsx';
import useIterationLock from 'hooks/useIterationLock.ts';
import useProtectRoute from 'hooks/useProtectRoute.ts';
import useUpdateIteration from 'queries/mutate/updateIteration.ts';
import { IterationDetailsFormValues } from 'schemas/Iteration.schema.ts';
import { ApiError } from 'schemas/ApiError.schema';
import { API_UPDATE_ITERATION_ERROR } from 'constants/errorMessages';
import { IIterationLevelParams } from 'utils/parameterUtils';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign';
import useCampaign from 'hooks/useCampaign.ts';
import ProgressIndicator from 'components/ProgressIndicator/index.tsx';
import useFetchTemplateForCampaign from 'queries/fetch/fetchTemplateForCampaign.ts';

function IterationDetailsPage() {
  const { campaign } = useCampaign();
  const [error, setError] = useState<ApiError | null>(null);
  const { teamId, campaignId, iterationId: iterId } = useParams() as IIterationLevelParams;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const { lockIterationIfRequired, error: lockError } = useIterationLock({ teamId, campaignId, iterationId: iterId });
  useProtectRoute();
  const initialRenderRef = useRef(true);
  const navigate = useNavigate();
  const navigateToNextPage = (iterationId: string) => {
    const redirectPage = searchParams.get('redirectPage');

    if (redirectPage) {
      navigate(redirectPage);
      return;
    }

    navigate(
      `/${teamId}/campaign/${campaignId}/iteration/${iterationId}/summary`,
    );
  };

  const updateIteration = useUpdateIteration();
  const fetchSpecificCampaign = useFetchSpecificCampaign({ teamId, campaignId });
  const campaignTemplate = useFetchTemplateForCampaign({
    teamId,
    campaignId,
    isEnabled: Boolean(campaign.CampaignConfig.Template),
  });

  const handleSuccessfulSubmission = async (iteration: IterationDetailsFormValues) => {
    try {
      setIsSubmitting(true);
      await updateIteration.mutateAsync({
        teamId: teamId!,
        iterationId: iterId!,
        campaignId: campaignId!,
        iteration,
      });

      await fetchSpecificCampaign.refetch();

      navigateToNextPage(iterId!);
    } catch {
      setError({ message: API_UPDATE_ITERATION_ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      lockIterationIfRequired();
    }
  }, [lockIterationIfRequired]);

  const existingIteration = campaign.CampaignConfig.Iterations?.find(
    (iteration) => iteration.ID === iterId,
  );

  if (!existingIteration) {
    return <ProgressIndicator text="Loading iteration details..." />;
  }

  return (
    <div>
      <div className="nhsuk-u-margin-bottom-6">
        <Back label="Iteration summary" backTo={`/${teamId}/campaign/${campaignId}/iteration/${iterId}/summary`} />
      </div>
      <IterationDetailsForm
        onSuccessfulSubmission={handleSuccessfulSubmission}
        isSubmitting={isSubmitting}
        iteration={existingIteration}
        template={campaignTemplate.data}
      />
      <ApiErrorDetails apiError={error || updateIteration.error || lockError} />
    </div>
  );
}
export default IterationDetailsPage;
